import dayjs from "dayjs"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"

/** Relations for Admin Flags table columns. */
export const ADMIN_FLAG_LIST_WITH = ["user", "flaggable"]

/** Relations for Admin Flag detail modal. */
export const ADMIN_FLAG_DETAIL_WITH = [
  "user",
  "flaggable",
  "media",
  "conversations.sender",
]

export const FLAG_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "opened", label: "Open", countTone: "awaiting" },
  { key: "under_review", label: "Under Review", countTone: "awaiting" },
  { key: "resolved", label: "Resolved", countTone: "approved" },
  { key: "dismissed", label: "Dismissed", countTone: "rejected" },
  { key: "withdrawn", label: "Withdrawn", countTone: "muted" },
]

export const FLAG_STATUS_OPTIONS = [
  { value: "opened", label: "Open" },
  { value: "under_review", label: "Under Review" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
  { value: "withdrawn", label: "Withdrawn" },
]

export const STATUS_LABEL = {
  opened: "Open",
  open: "Open",
  under_review: "Under Review",
  resolved: "Resolved",
  dismissed: "Dismissed",
  withdrawn: "Withdrawn",
}

/** UI / SCSS status key used for badge classes. */
export const statusToneKey = status => {
  const key = String(status || "")
    .trim()
    .toLowerCase()
  if (key === "opened" || key === "open") return "open"
  if (key === "under_review") return "under-review"
  if (key === "resolved") return "resolved"
  if (key === "dismissed") return "dismissed"
  if (key === "withdrawn") return "withdrawn"
  return key || "open"
}

export const isActionableFlagStatus = status => {
  const key = String(status || "")
    .trim()
    .toLowerCase()
  return key === "under_review"
}

export const getFlagInitials = name =>
  (name || "")
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()

const personName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const ADDRESS_TYPE_ORDER = { primary: 0, secondary: 1, tertiary: 2 }

const formatFlagCityLabel = cityLike => {
  if (!cityLike) return null
  if (typeof cityLike === "string") {
    const trimmed = cityLike.trim()
    return trimmed.includes(",") ? trimmed.split(",")[0].trim() : trimmed || null
  }
  const cityName = String(cityLike.city || cityLike.name || "").trim()
  return cityName || null
}

/** All member cities from flaggable addresses (primary → secondary → tertiary). */
export const resolveFlaggableCityLabels = flaggable => {
  const addresses = Array.isArray(flaggable?.addresses)
    ? flaggable.addresses
    : []

  const sorted = [...addresses].sort((left, right) => {
    const leftKey = String(left?.address_type || "").toLowerCase()
    const rightKey = String(right?.address_type || "").toLowerCase()
    const leftOrder = ADDRESS_TYPE_ORDER[leftKey] ?? 99
    const rightOrder = ADDRESS_TYPE_ORDER[rightKey] ?? 99
    return leftOrder - rightOrder
  })

  const seen = new Set()
  const labels = []

  sorted.forEach(address => {
    const label = formatFlagCityLabel(address?.city)
    if (label && !seen.has(label)) {
      seen.add(label)
      labels.push(label)
    }
  })

  if (labels.length === 0) {
    const fallback =
      formatFlagCityLabel(flaggable?.city) || formatFlagCityLabel(flaggable)
    if (fallback) labels.push(fallback)
  }

  return labels.length ? labels : ["--"]
}

const reasonLabel = reasons => {
  if (Array.isArray(reasons) && reasons.length > 0) {
    return reasons.filter(Boolean).join(", ")
  }
  if (typeof reasons === "string" && reasons.trim()) return reasons.trim()
  return "--"
}

const flaggedContentLabel = item => {
  const type = String(item?.flaggable_type || "")
    .trim()
    .toLowerCase()
  const flaggable = item?.flaggable
  if (type === "user") {
    const name = personName(flaggable)
    return name !== "--" ? `Member: ${name}` : "Member profile"
  }
  if (flaggable?.name || flaggable?.title) {
    return flaggable.name || flaggable.title
  }
  return type ? type.replace(/_/g, " ") : "--"
}

const formatFlagDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

const mapConversations = (conversations = []) =>
  (Array.isArray(conversations) ? conversations : []).map(item => ({
    id: item.id,
    message: item.message || "",
    createdAt: item.created_at || "",
    createdAtLabel: formatFlagDate(item.created_at),
    senderName: personName(item.sender),
    senderAvatar: getUserAvatarUrl(item.sender),
    raw: item,
  }))

const mapMedia = (media = []) =>
  (Array.isArray(media) ? media : []).map(item => ({
    id: item.id,
    url: item.path_url || item.thumbnail_path_url || "",
    thumbnailUrl: item.thumbnail_path_url || item.path_url || "",
    raw: item,
  }))

/**
 * Map GET /api/v1/admin/flags item → table / modal row.
 */
export const mapAdminFlagRow = item => {
  const status = String(item?.status || "")
    .trim()
    .toLowerCase()
  const user = item?.user || null
  const flaggable = item?.flaggable || null
  const reporter = personName(user)
  const flaggedUser = personName(flaggable)
  const cities = resolveFlaggableCityLabels(flaggable)

  return {
    id: item?.id,
    reporter,
    reporterAvatar: getUserAvatarUrl(user),
    flaggedUser,
    flaggedUserAvatar: getUserAvatarUrl(flaggable),
    city: cities[0] || "--",
    cities,
    cityLabel: cities.join(", "),
    content: flaggedContentLabel(item),
    reason: reasonLabel(item?.reasons),
    date: formatFlagDate(item?.created_at),
    status: status || "opened",
    statusTone: statusToneKey(status),
    description: item?.description || "--",
    flaggableType: item?.flaggable_type || "",
    decisionReason: item?.decision_reason || "",
    media: mapMedia(item?.media),
    conversations: mapConversations(item?.conversations),
    raw: item,
  }
}

/** City Portal fixtures until that screen is API-wired. */
export const CITY_FLAG_STATUS_TABS = [
  { key: "all", label: "All", count: 23, countTone: "muted" },
  { key: "open", label: "Open", count: 4, countTone: "awaiting" },
  { key: "resolved", label: "Resolved", count: 12, countTone: "approved" },
  { key: "dismissed", label: "Dismissed", count: 7, countTone: "rejected" },
]

export const FLAG_ROWS = [
  {
    id: "1",
    reporter: "Sarah Mitchell",
    flaggedUser: "David Park",
    city: "Dallas, US",
    content: "Inappropriate event description",
    reason: "Offensive Content",
    date: "Jul 1, 2025",
    status: "open",
    description:
      "The event description contains inappropriate language and misleading claims about the venue capacity and pricing.",
  },
  {
    id: "2",
    reporter: "James Wilson",
    flaggedUser: "Mateo Ruiz",
    city: "San Francisco, US",
    content: "Spam collaboration request",
    reason: "Spam",
    date: "Jun 30, 2025",
    status: "open",
    description:
      "Repeated unsolicited collaboration messages were sent to multiple members within a short period.",
  },
  {
    id: "3",
    reporter: "Emily Chen",
    flaggedUser: "Clara Byrne",
    city: "Berlin, DE",
    content: "Misleading project listing",
    reason: "Misinformation",
    date: "Jun 29, 2025",
    status: "resolved",
    description:
      "Project details misrepresent deliverables and timeline compared to what was discussed in onboarding.",
  },
  {
    id: "4",
    reporter: "Ryan Carter",
    flaggedUser: "Noah Kim",
    city: "London, UK",
    content: "Harassment in session chat",
    reason: "Harassment",
    date: "Jul 4, 2025",
    status: "open",
    description:
      "Session chat includes targeted insults and repeated hostile messages directed at another collaborator.",
  },
  {
    id: "5",
    reporter: "Rachel Kim",
    flaggedUser: "Sofia Rossi",
    city: "San Francisco, US",
    content: "Fake portfolio images",
    reason: "Fraud",
    date: "Jul 5, 2025",
    status: "dismissed",
    description:
      "Portfolio images appear to be stock photos unrelated to the member's listed work history.",
  },
  {
    id: "6",
    reporter: "Marcus Bell",
    flaggedUser: "Michael Doe",
    city: "Chicago, US",
    content: "Abusive review comments",
    reason: "Harassment",
    date: "Jun 28, 2025",
    status: "resolved",
    description:
      "Review thread contains personal attacks rather than constructive feedback about the event.",
  },
  {
    id: "7",
    reporter: "Lisa Anderson",
    flaggedUser: "Ethan Wright",
    city: "Austin, US",
    content: "Copied call for artists post",
    reason: "Copyright",
    date: "Jul 1, 2025",
    status: "open",
    description:
      "Call for artists copy closely matches a previously published post from another city hub.",
  },
  {
    id: "8",
    reporter: "David Park",
    flaggedUser: "Amira Patel",
    city: "Toronto, CA",
    content: "Impersonation in messages",
    reason: "Impersonation",
    date: "Jun 25, 2025",
    status: "resolved",
    description:
      "Messages were sent claiming to represent platform staff and requesting account credentials.",
  },
  {
    id: "9",
    reporter: "Ava Chen",
    flaggedUser: "Liam Jones",
    city: "Seattle, US",
    content: "Spam event postings",
    reason: "Spam",
    date: "Jul 3, 2025",
    status: "dismissed",
    description:
      "Multiple duplicate event listings were posted across several city pages within one hour.",
  },
  {
    id: "10",
    reporter: "Noah Kim",
    flaggedUser: "Maria Garcia",
    city: "Miami, US",
    content: "Offensive profile bio",
    reason: "Offensive Content",
    date: "Jun 27, 2025",
    status: "resolved",
    description:
      "Profile biography includes slurs and language that violates community guidelines.",
  },
  {
    id: "11",
    reporter: "Olivia Reed",
    flaggedUser: "Emily Davis",
    city: "Denver, US",
    content: "Scam reward claim",
    reason: "Fraud",
    date: "Jul 2, 2025",
    status: "dismissed",
    description:
      "Reward claim submission includes falsified activity logs and mismatched account metadata.",
  },
  {
    id: "12",
    reporter: "Ethan Brooks",
    flaggedUser: "Hiroshi Tanaka",
    city: "Portland, US",
    content: "Hate speech in comments",
    reason: "Harassment",
    date: "Jun 26, 2025",
    status: "resolved",
    description:
      "Comment thread contains discriminatory language targeting a protected group.",
  },
  {
    id: "13",
    reporter: "Mia Santos",
    flaggedUser: "Alara Voss",
    city: "Phoenix, US",
    content: "Duplicate photography upload",
    reason: "Spam",
    date: "Jul 6, 2025",
    status: "resolved",
    description:
      "The same photography set was uploaded repeatedly to inflate visibility in city galleries.",
  },
  {
    id: "14",
    reporter: "Liam Foster",
    flaggedUser: "Elena Petrova",
    city: "Boston, US",
    content: "False city transfer claim",
    reason: "Misinformation",
    date: "Jun 24, 2025",
    status: "dismissed",
    description:
      "Transfer request justification includes inaccurate residency details and unsupported claims.",
  },
  {
    id: "15",
    reporter: "Sophia Lee",
    flaggedUser: "Jerry Michael",
    city: "Nashville, US",
    content: "Inappropriate session title",
    reason: "Offensive Content",
    date: "Jul 7, 2025",
    status: "resolved",
    description:
      "Session title and summary include explicit language inappropriate for a public community event.",
  },
]

